import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import api from '../services/api';
import BlogLayout from '../components/BlogLayout';
import SEO from '../components/SEO';
import './BlogPage.css';
import { Calendar, User, BookOpen } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { getBlogImageProps } from '../utils/imageUtils';
import { SITE_URL } from '../config/site';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  tags: string[] | null;
  categorySlug: string | null;
  categoryName: string | null;
  authorName: string;
  publishedAt: string;
}

export default function BlogCategoryPage() {
    const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const getCategoryInfo = (categorySlug: string | undefined) => {
    switch(categorySlug) {
      case 'response-and-automation':
        return { title: t('common.responseAutomation'), desc: t('blog.category.tipsStrategiesAndGuidesOnGoogle') };
      case 'multi-platform-management':
        return { title: t('common.multiPlatformManagement'), desc: t('blog.category.learnHowToHandleReviewsAcross') };
      case 'insights-and-action':
        return { title: t('common.insightsAction'), desc: t('blog.category.extractActionableBusinessInsightsFromYou') };
      default:
        return { title: t('common.category'), desc: t('blog.category.articlesInThisCategory') };
    }
  };
  const categoryInfo = getCategoryInfo(slug);

  useEffect(() => {
    setPage(0); // Reset page when category changes
  }, [slug]);

  useEffect(() => {
    if (slug) fetchPosts();
  }, [slug, page, i18n.language]);

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/blog/posts', { params: { page, size: 50, category: slug, lang: i18n.language } });
      setPosts(res.data.content);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error('Failed to load blog posts for category');
    } finally {
      setIsLoading(false);
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          (window as any).__PRERENDER_READY = true;
        }, 100);
      }
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(i18n.language, {
      month: 'long', day: 'numeric', year: 'numeric',
    });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: posts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/blog/${post.slug}`,
      name: post.title
    }))
  };

  return (
    <BlogLayout>
      <SEO 
        title={`${categoryInfo.title} | Blog`}
        description={categoryInfo.desc}
        schema={jsonLd}
      />
      <div className="blog-container animate-fade-in">
        <header className="blog-list-hero blog-category-header">
          <Link to="/blog" className="blog-back-link blog-category-back-link">{' ' + t('blog.category.backToAllArticles')}</Link>
          <div className="blog-list-hero-label">{t('common.category')}</div>
          <h1>{categoryInfo.title}</h1>
          <p>{categoryInfo.desc}</p>
        </header>

        {isLoading ? (
          <div className="blog-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="blog-card-skeleton">
                <div className="blog-card-skeleton-cover" />
                <div className="blog-card-skeleton-body">
                  <div className="skeleton-line" style={{ width: '40%', height: 12 }} />
                  <div className="skeleton-line" style={{ width: '90%', height: 18 }} />
                  <div className="skeleton-line" style={{ width: '100%', height: 14 }} />
                  <div className="skeleton-line" style={{ width: '60%', height: 14 }} />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="blog-empty-state">
            <BookOpen size={56} />
            <h2>{t('blog.category.noArticlesFound')}</h2>
            <p>We couldn't find any articles in this category.</p>
          </div>
        ) : (
          <>
            <div className="blog-grid">
              {posts.map(post => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="card blog-card">
                  {post.coverImageUrl ? (() => {
                    const imgProps = getBlogImageProps(post.coverImageUrl);
                    return imgProps ? (
                      <picture>
                        {imgProps.srcSet && (
                          <source type="image/webp" srcSet={imgProps.srcSet} sizes="(max-width: 768px) 100vw, 400px" />
                        )}
                        <img src={imgProps.src} alt={post.title} className="blog-card-cover" width={800} height={450} loading="lazy" />
                      </picture>
                    ) : (
                      <img src={post.coverImageUrl} alt={post.title} className="blog-card-cover" width={800} height={450} loading="lazy" />
                    );
                  })() : (
                    <div className="blog-card-cover-placeholder">
                      <BookOpen size={40} />
                    </div>
                  )}
                  <div className="blog-card-body">
                    {post.tags && post.tags.length > 0 && (
                      <div className="blog-card-tags">
                        {post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="blog-card-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="blog-card-title">{post.title}</h2>
                    {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}
                  </div>
                  <div className="blog-card-footer">
                    <span className="blog-card-author">
                      <User size={12} className="blog-icon-1" />
                      {post.authorName}
                    </span>
                    <span>
                      <Calendar size={12} className="blog-icon-1" />
                      {formatDate(post.publishedAt)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="blog-pagination">
                <button className="btn btn-secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>{t('common.previous2')}</button>
                <span className="blog-pagination-info">{`${t('common.page')} ${page + 1} ${t('common.of')} ${totalPages}`}</span>
                <button className="btn btn-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>{t('common.next2')}</button>
              </div>
            )}
          </>
        )}
        
        {/* Promotional CTA */}
        <div className="blog-cta-section blog-cta-section-spacing">
          <div className="blog-cta-content">
            <h2>{t('common.readyToAutomateYourReviewManagement')}</h2>
            <p>{t("common.joinThousandsOfLocalBusinessesUsing")}</p>
            <Link to="/signup" className="btn btn-primary btn-lg blog-cta-btn-spacing">{t('common.startYourFreeTrialToday')}</Link>
          </div>
        </div>
      </div>
    </BlogLayout>
  );
}
